import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Grid,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  QrCode as QrCodeIcon,
  CameraAlt as ScanIcon
} from '@mui/icons-material';
import equipmentService from '../services/equipment.service';
import BarcodeScanner from './BarcodeScanner';
import useBarcodeScanner from '../hooks/useBarcodeScanner';
import FeatureGate from './FeatureGate';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

const EquipmentTemplateManager = () => {
  const { isFeatureEnabled } = useFeatureFlags();

  // Load saved settings from localStorage
  const loadTemplateSettings = () => {
    const savedSettings = localStorage.getItem('equipmentTemplateSettings');
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch (e) {
        console.error('Failed to parse saved template settings:', e);
      }
    }
    return null;
  };

  const savedSettings = loadTemplateSettings();

  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [sortedTemplates, setSortedTemplates] = useState([]);
  const [paginatedTemplates, setPaginatedTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState(savedSettings?.searchTerm || '');
  const [categoryFilter, setCategoryFilter] = useState(savedSettings?.categoryFilter || '');
  const [categories, setCategories] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(savedSettings?.rowsPerPage || 25);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state
  const [orderBy, setOrderBy] = useState(savedSettings?.orderBy || 'manufacturer');
  const [order, setOrder] = useState(savedSettings?.order || 'asc');
  
  const [formData, setFormData] = useState({
    manufacturer: '',
    model: '',
    description: '',
    category_id: '',
    notes: '',
    barcode: ''
  });

  const [errors, setErrors] = useState({});
  
  // Barcode scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const { handleScan } = useBarcodeScanner();

  // Helper function to sanitize content for display
  const sanitizeContent = (content) => {
    if (!content) return '';
    return DOMPurify.sanitize(content, { 
      ALLOWED_TAGS: [], // No HTML tags allowed in display
      ALLOWED_ATTR: [] 
    });
  };

  useEffect(() => {
    console.log('EquipmentTemplateManager: Component mounted, starting initialization...');
    console.log('EquipmentTemplateManager: Categories enabled:', isFeatureEnabled('EQUIPMENT_CATEGORIES'));
    loadTemplates();
    loadCategories();
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      searchTerm,
      categoryFilter,
      rowsPerPage,
      orderBy,
      order
    };
    localStorage.setItem('equipmentTemplateSettings', JSON.stringify(settings));
  }, [searchTerm, categoryFilter, rowsPerPage, orderBy, order]);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchTerm, categoryFilter]);

  useEffect(() => {
    sortTemplates();
  }, [filteredTemplates, orderBy, order]);

  useEffect(() => {
    paginateTemplates();
  }, [sortedTemplates, page, rowsPerPage]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      console.log('EquipmentTemplateManager: Starting to load templates...');
      
      // Load all templates - increased limit to handle large template libraries
      // Note: Consider implementing server-side pagination if performance becomes an issue
      const response = await equipmentService.getTemplates({ limit: 10000 });
      console.log('EquipmentTemplateManager: Raw response from getTemplates:', response);
      
      // Check if we have pagination metadata
      if (response.meta && response.meta.total) {
        setTotalCount(response.meta.total);
        console.log('EquipmentTemplateManager: Total templates from server:', response.meta.total);
      }
      
      // Handle V2 barcode API response format: { success: true, data: [...], meta: {...} }
      let templatesData = [];
      if (response.success && response.data && Array.isArray(response.data)) {
        // Standard paginated response format
        templatesData = response.data;
        console.log('EquipmentTemplateManager: Found templates in standard response format:', templatesData.length);
      } else if (response.data && response.data.templates) {
        templatesData = response.data.templates;
        console.log('EquipmentTemplateManager: Found templates in response.data.templates:', templatesData.length);
      } else if (response.data && Array.isArray(response.data)) {
        templatesData = response.data;
        console.log('EquipmentTemplateManager: Found templates in response.data array:', templatesData.length);
      } else if (Array.isArray(response)) {
        templatesData = response;
        console.log('EquipmentTemplateManager: Found templates in response array:', templatesData.length);
      } else if (response.templates && Array.isArray(response.templates)) {
        templatesData = response.templates;
        console.log('EquipmentTemplateManager: Found templates in response.templates:', templatesData.length);
      } else {
        console.log('EquipmentTemplateManager: No templates found in any expected location. Full response:', response);
      }
      
      console.log('EquipmentTemplateManager: Setting templates data:', templatesData);
      console.log('EquipmentTemplateManager: Templates array length:', templatesData.length);
      
      if (templatesData.length === 0) {
        console.log('EquipmentTemplateManager: No templates found. This could mean:');
        console.log('  1. No templates have been created yet');
        console.log('  2. Templates exist but API is not returning them');
        console.log('  3. Database connection issue');
        console.log('  4. Different response format than expected');
      }
      
      setTemplates(templatesData);
    } catch (error) {
      console.error('EquipmentTemplateManager: Error loading templates:', error);
      console.error('EquipmentTemplateManager: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      showSnackbar('Failed to load equipment templates', 'error');
      setTemplates([]); // Ensure templates is always an array
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    // Only load categories if the feature is enabled
    if (!isFeatureEnabled('EQUIPMENT_CATEGORIES')) {
      console.log('Equipment categories feature disabled - skipping category load');
      setCategories([]);
      return;
    }
    
    try {
      const response = await equipmentService.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const filterTemplates = () => {
    // Ensure templates is an array before spreading
    let filtered = Array.isArray(templates) ? [...templates] : [];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(template => 
        template.barcode_pattern?.toLowerCase().includes(search) ||
        template.manufacturer?.toLowerCase().includes(search) ||
        template.model?.toLowerCase().includes(search) ||
        template.description?.toLowerCase().includes(search) ||
        template.template_data?.description?.toLowerCase().includes(search)
      );
    }
    
    if (categoryFilter) {
      filtered = filtered.filter(template => template.category_name === categoryFilter);
    }
    
    setFilteredTemplates(filtered);
    // Reset to first page when filters change
    setPage(0);
  };

  const sortTemplates = () => {
    const sorted = [...filteredTemplates].sort((a, b) => {
      let aValue = '';
      let bValue = '';
      
      // Get values based on the column being sorted
      switch (orderBy) {
        case 'barcode':
          aValue = a.barcode_pattern || '';
          bValue = b.barcode_pattern || '';
          break;
        case 'manufacturer':
          aValue = a.manufacturer || a.template_data?.manufacturer || '';
          bValue = b.manufacturer || b.template_data?.manufacturer || '';
          break;
        case 'model':
          aValue = a.model || a.template_data?.model || '';
          bValue = b.model || b.template_data?.model || '';
          break;
        case 'description':
          aValue = a.description || a.template_data?.description || '';
          bValue = b.description || b.template_data?.description || '';
          break;
        case 'category':
          aValue = a.category_name || '';
          bValue = b.category_name || '';
          break;
        default:
          aValue = a.name || '';
          bValue = b.name || '';
      }
      
      // Convert to lowercase for case-insensitive sorting
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
      
      if (order === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
    
    setSortedTemplates(sorted);
  };

  const paginateTemplates = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginated = sortedTemplates.slice(startIndex, endIndex);
    setPaginatedTemplates(paginated);
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      console.log('🔍 EquipmentTemplateManager - Template data structure:', {
        id: template.id,
        notes: template.notes,
        template_data_notes: template.template_data?.notes,
        template_data: template.template_data,
        allKeys: Object.keys(template),
        fullTemplate: template
      });

      setEditingTemplate(template);
      setFormData({
        manufacturer: template.manufacturer || template.template_data?.manufacturer || '',
        model: template.model || template.template_data?.model || '',
        description: template.description || template.template_data?.description || '',
        category_id: template.category_id || template.template_data?.category_id || '',
        notes: template.notes || template.template_data?.notes || '',
        barcode: template.barcode_pattern || ''
      });

      console.log('🔍 EquipmentTemplateManager - Form data after setting:', {
        notes: template.notes || template.template_data?.notes || '',
        notesSource: template.notes ? 'template.notes' : template.template_data?.notes ? 'template.template_data.notes' : 'empty'
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        manufacturer: '',
        model: '',
        description: '',
        category_id: '',
        notes: '',
        barcode: ''
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    
    // No required fields for templates - manufacturer is optional
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // Sanitize all text fields before sending to server
      const templateName = editingTemplate 
        ? editingTemplate.name // Keep original name for updates
        : `${formData.manufacturer || 'Unknown'} ${formData.model || 'Model'}`.trim(); // Generate name for new templates
        
      const templateData = {
        name: DOMPurify.sanitize(templateName),
        barcodePattern: formData.barcode ? DOMPurify.sanitize(formData.barcode.trim()) : null,
        templateData: {
          manufacturer: formData.manufacturer ? DOMPurify.sanitize(formData.manufacturer) : null,
          model: formData.model ? DOMPurify.sanitize(formData.model) : null,
          description: formData.description ? DOMPurify.sanitize(formData.description) : null,
          category_id: formData.category_id || null,
          notes: formData.notes ? DOMPurify.sanitize(formData.notes) : null
        }
      };

      console.log('EquipmentTemplateManager.handleSave: Saving template with data:', templateData);
      console.log('EquipmentTemplateManager.handleSave: Form data was:', formData);
      console.log('EquipmentTemplateManager.handleSave: Edit mode:', editingTemplate ? 'update' : 'create');
      
      if (editingTemplate) {
        console.log('EquipmentTemplateManager.handleSave: Updating template ID:', editingTemplate.id);
        const result = await equipmentService.updateTemplate(editingTemplate.id, templateData);
        console.log('EquipmentTemplateManager.handleSave: Update result:', result);
        showSnackbar('Template updated successfully', 'success');
      } else {
        console.log('EquipmentTemplateManager.handleSave: Creating new template');
        const result = await equipmentService.createTemplate(templateData);
        console.log('EquipmentTemplateManager.handleSave: Creation result:', result);
        showSnackbar('Template created successfully', 'success');
      }
      
      handleCloseDialog();
      console.log('EquipmentTemplateManager.handleSave: Reloading templates...');
      await loadTemplates();
    } catch (error) {
      console.error('EquipmentTemplateManager.handleSave: Error saving template:', error);
      console.error('EquipmentTemplateManager.handleSave: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // More detailed error message for debugging
      let errorMessage = 'Failed to save template';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Log template count for debugging
      console.log('Current template count:', templates.length);

      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete template "${sanitizeContent(template.name)}"?`)) {
      return;
    }

    try {
      await equipmentService.deleteTemplate(template.id);
      showSnackbar('Template deleted successfully', 'success');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      if (error.message && error.message.includes('not implemented')) {
        showSnackbar('Delete functionality is not available in the current version', 'info');
      } else {
        showSnackbar(error.response?.data?.message || 'Failed to delete template', 'error');
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBarcodeDetected = (barcode) => {
    setFormData(prev => ({ ...prev, barcode }));
    setScannerOpen(false);
    showSnackbar(`Barcode detected: ${barcode}`, 'success');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Equipment Template Manager
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ 
            flexGrow: 1, 
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#FFFFFF',
              },
              '&.Mui-focused': {
                backgroundColor: '#FFFFFF',
              }
            }
          }}
        />
        
        <FeatureGate feature="EQUIPMENT_CATEGORIES">
          <FormControl sx={{ 
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#FFFFFF',
              },
              '&.Mui-focused': {
                backgroundColor: '#FFFFFF',
              }
            }
          }}>
            <InputLabel>Category Filter</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Category Filter"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FeatureGate>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Template
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '5px',
          background: 'linear-gradient(135deg, #2C2C2C, #4A4A4A)',
          borderRadius: '4px 4px 0 0',
          zIndex: 1,
        }
      }}>
        <Table stickyHeader sx={{
          '& .MuiTableHead-root': {
            backgroundColor: '#F5F5F5',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: '#F5F5F5',
            color: '#2C2C2C',
            fontWeight: 600,
            borderBottom: '2px solid #E0E0E0',
          }
        }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'barcode'}
                  direction={orderBy === 'barcode' ? order : 'asc'}
                  onClick={() => handleRequestSort('barcode')}
                >
                  Barcode
                </TableSortLabel>
              </TableCell>
              <FeatureGate feature="EQUIPMENT_CATEGORIES">
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'category'}
                    direction={orderBy === 'category' ? order : 'asc'}
                    onClick={() => handleRequestSort('category')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
              </FeatureGate>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'manufacturer'}
                  direction={orderBy === 'manufacturer' ? order : 'asc'}
                  onClick={() => handleRequestSort('manufacturer')}
                >
                  Manufacturer
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'model'}
                  direction={orderBy === 'model' ? order : 'asc'}
                  onClick={() => handleRequestSort('model')}
                >
                  Model
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'description'}
                  direction={orderBy === 'description' ? order : 'asc'}
                  onClick={() => handleRequestSort('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">Loading...</TableCell>
              </TableRow>
            ) : filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">No templates found</TableCell>
              </TableRow>
            ) : (
              paginatedTemplates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    {sanitizeContent(template.barcode_pattern) || <em style={{ color: '#888' }}>empty</em>}
                  </TableCell>
                  <FeatureGate feature="EQUIPMENT_CATEGORIES">
                    <TableCell>{sanitizeContent(template.category_name) || '-'}</TableCell>
                  </FeatureGate>
                  <TableCell>{sanitizeContent(template.manufacturer || template.template_data?.manufacturer) || '-'}</TableCell>
                  <TableCell>{sanitizeContent(template.model || template.template_data?.model) || '-'}</TableCell>
                  <TableCell>{sanitizeContent(template.description || template.template_data?.description) || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={'Active'} 
                      color={'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleOpenDialog(template)} size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(template)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount || sortedTemplates.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          showFirstButton
          showLastButton
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            maxHeight: '90vh',
            height: 'auto',
            minHeight: '600px'
          }
        }}
        scroll="paper"
      >
        <DialogTitle>
          {editingTemplate ? 'Edit Equipment Template' : 'Add Equipment Template'}
        </DialogTitle>
        <DialogContent sx={{ pb: 2, overflow: 'visible', minHeight: '450px' }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Barcode Pattern"
                  value={formData.barcode}
                  onChange={(e) => handleInputChange('barcode', e.target.value)}
                  helperText="Enter a barcode to create pattern (e.g. SM58-2024-001)"
                  placeholder="Scan or enter barcode"
                />
                <Button
                  variant="outlined"
                  onClick={() => setScannerOpen(true)}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  <ScanIcon />
                </Button>
              </Box>
            </Grid>
            
            {/* Barcode Scanner - positioned right after barcode input for better visibility */}
            {openDialog && scannerOpen && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Barcode Scanner
                    </Typography>
                    <Button 
                      size="small" 
                      onClick={() => setScannerOpen(false)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Close
                    </Button>
                  </Box>
                  <BarcodeScanner
                    onScanSuccess={handleBarcodeDetected}
                    onScanError={(err) => console.error('Scan error:', err)}
                    autoStart={scannerOpen}
                    continuous={false}
                    height="180px"
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                error={!!errors.manufacturer}
                helperText={errors.manufacturer}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Model"
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
            <FeatureGate feature="EQUIPMENT_CATEGORIES">
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category_id || ''}
                    onChange={(e) => handleInputChange('category_id', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">None</MenuItem>
                    {categories.map(cat => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </FeatureGate>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default EquipmentTemplateManager;